import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import API_BASE from "./config";
import { TermsBody, PrivacyBody, TERMS_VERSION, PRIVACY_VERSION } from "./legal/legalContent";
import LegalDocModal from "./legal/LegalDocModal";

const InviteRegistration = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [reason, setReason] = useState("");
  const [inviteData, setInviteData] = useState(null);

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [legalModal, setLegalModal] = useState(null); // 'terms' | 'privacy' | null
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await axios.get(`${API_BASE}/org-invite/${token}`);
        if (res.data.valid) {
          setValid(true);
          setInviteData(res.data);
        } else {
          setValid(false);
          setReason(res.data.reason);
        }
      } catch (err) {
        setValid(false);
        setReason("server_error");
      } finally {
        setLoading(false);
      }
    };

    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return alert("Name is required.");
    if (!password || password.length < 8) return alert("Password must be at least 8 characters.");
    if (password !== confirmPassword) return alert("Passwords do not match.");
    if (!agreeTerms || !agreePrivacy) return alert("You must accept the Terms of Service and the Privacy Policy.");

    try {
      setSubmitting(true);
      setSubmitMessage("");

      const res = await axios.post(`${API_BASE}/org-invite/register`, {
        token,
        name,
        password,
        acceptedTerms: agreeTerms,
        acceptedPrivacy: agreePrivacy,
        termsVersion: TERMS_VERSION,
        privacyVersion: PRIVACY_VERSION
      });

      setSubmitMessage(res.data.message || "Registration successful. Check your email to verify.");
    } catch (err) {
      console.error("Registration error:", err);
      setSubmitMessage("Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: 20 }}>Validating invitation…</div>;

  if (!valid) {
    return (
      <div style={{ padding: 20 }}>
        <h2>Invitation Error</h2>
        {reason === "expired" && <p>This invitation link has expired.</p>}
        {reason === "completed" && <p>This invitation has already been used.</p>}
        {reason === "invalid" && <p>This invitation link is invalid.</p>}
        {reason === "server_error" && <p>Server error validating invitation.</p>}
      </div>
    );
  }

  return (
    <div style={{ padding: 20, maxWidth: 480, margin: "0 auto" }}>
        <h1>Join CloudGenomics</h1>
        <p>You are invited to join your organization on CloudGenomics.</p>


        <form onSubmit={handleSubmit} style={{ marginTop: 20 }}>
            <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", marginBottom: 4 }}>Email</label>
            <input
                type="email"
                value={inviteData.invitee_email}
                disabled
                style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #ccc" }}
            />
            </div>

            <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", marginBottom: 4 }}>Full Name</label>
            <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #ccc" }}
            />
            </div>

            <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", marginBottom: 4 }}>Password</label>
            <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #ccc" }}
            />
            </div>

            <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", marginBottom: 4 }}>Confirm Password</label>
            <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #ccc" }}
            />
            </div>

            <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", marginBottom: 6 }}>Legal Agreements</label>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                <input type="checkbox" checked={agreeTerms} onChange={e => setAgreeTerms(e.target.checked)} style={{ marginTop: 3 }} />
                <span>
                  I have read and agree to the{" "}
                  <button type="button" onClick={() => setLegalModal("terms")} style={{ background: "none", border: "none", padding: 0, color: "#2563eb", textDecoration: "underline", cursor: "pointer", font: "inherit" }}>Terms of Service</button>.
                </span>
            </label>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <input type="checkbox" checked={agreePrivacy} onChange={e => setAgreePrivacy(e.target.checked)} style={{ marginTop: 3 }} />
                <span>
                  I have read and agree to the{" "}
                  <button type="button" onClick={() => setLegalModal("privacy")} style={{ background: "none", border: "none", padding: 0, color: "#2563eb", textDecoration: "underline", cursor: "pointer", font: "inherit" }}>Privacy Policy</button>.
                </span>
            </label>
            </div>

            <button
            type="submit"
            disabled={submitting || !agreeTerms || !agreePrivacy}
            style={{
                marginTop: 12,
                padding: "10px 16px",
                borderRadius: 8,
                border: "none",
                background: (submitting || !agreeTerms || !agreePrivacy) ? "#94a3b8" : "#2563eb",
                color: "white",
                fontWeight: 500,
                cursor: (submitting || !agreeTerms || !agreePrivacy) ? "not-allowed" : "pointer"
            }}
            >
            {submitting ? "Registering…" : "Register & Join Organization"}
            </button>

            {submitMessage && (
            <p style={{ marginTop: 12, color: "#16a34a" }}>{submitMessage}</p>
            )}
        </form>

        {legalModal === "terms" && (
          <LegalDocModal title="Terms of Service" onClose={() => setLegalModal(null)}><TermsBody /></LegalDocModal>
        )}
        {legalModal === "privacy" && (
          <LegalDocModal title="Privacy Policy" onClose={() => setLegalModal(null)}><PrivacyBody /></LegalDocModal>
        )}
    </div>
  );
};

export default InviteRegistration;